import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiUrl } from '../utils/api';
import { ArrowLeft, Check, X, Eye, Calendar, MapPin, User, Flag, AlertTriangle } from 'lucide-react';

interface AdminCampaignSummary {
  id: string;
  title: string;
  description: string;
  location: {
    country: string;
    state: string;
    city: string;
    address: string;
  };
  deadline: Date;
  status: 'active' | 'expired' | 'completed' | 'cancelled';
  privacyMode: 'private' | 'public';
  campaignType: 'solidarity' | 'family' | 'celebration';
  isVerified: boolean;
  createdAt: Date;
  creatorEmail: string;
  creatorName: string;
}

interface AdminReport {
  id: string;
  campaignId: string;
  userId: string;
  reason: string;
  comment: string;
  status: 'pending' | 'reviewed' | 'dismissed';
  adminNote?: string | null;
  resolvedAt?: string | null;
  createdAt: string;
  campaignTitle: string;
  campaignStatus: string;
  reporterEmail: string;
  reporterName: string;
}

export function AdminPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [campaigns, setCampaigns] = useState<AdminCampaignSummary[]>([]);
  const [reports, setReports] = useState<AdminReport[]>([]);
  const [pendingReportsCount, setPendingReportsCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingCampaign, setUpdatingCampaign] = useState<string | null>(null);
  const [updatingReport, setUpdatingReport] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'campaigns' | 'reports'>(
    searchParams.get('tab') === 'reports' ? 'reports' : 'campaigns'
  );

  useEffect(() => {
    fetchCampaigns();
    fetchReports();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(apiUrl('/api/admin/campaigns'), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (result.success) {
        setCampaigns(result.data);
      } else {
        if (response.status === 403) {
          setError('Acceso denegado. Se requiere rol de administrador.');
        } else if (response.status === 401) {
          navigate('/login');
        } else {
          setError(result.error?.message || 'Error al cargar las campañas');
        }
      }
    } catch (err) {
      setError('Error de conexión con el servidor');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchReports = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(apiUrl('/api/admin/reports'), {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const result = await response.json();
      if (result.success) {
        setReports(result.data);
        setPendingReportsCount(result.data.filter((r: AdminReport) => r.status === 'pending').length);
      }
    } catch { /* silent */ }
  };

  const resolveReport = async (reportId: string, status: 'reviewed' | 'dismissed') => {
    setUpdatingReport(reportId);
    const adminNote = status === 'dismissed'
      ? prompt('Nota opcional (razón del descarte):')
      : prompt('Nota opcional (acción tomada):');

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(apiUrl(`/api/admin/reports/${reportId}`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ status, adminNote: adminNote || undefined }),
      });

      const result = await response.json();
      if (result.success) {
        setReports(prev => prev.map(r => r.id === reportId ? { ...r, status, adminNote, resolvedAt: new Date().toISOString() } : r));
        setPendingReportsCount(prev => prev - 1);
      } else {
        setError(result.error?.message || 'Error al resolver el reporte');
      }
    } catch {
      setError('Error de conexión');
    } finally {
      setUpdatingReport(null);
    }
  };

  const updateVerificationStatus = async (campaignId: string, isVerified: boolean) => {
    setUpdatingCampaign(campaignId);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(apiUrl('/api/admin/campaigns/verify'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ campaignId, isVerified }),
      });

      const result = await response.json();

      if (result.success) {
        setCampaigns(prev => prev.map(campaign => 
          campaign.id === campaignId 
            ? { ...campaign, isVerified }
            : campaign
        ));
      } else {
        setError(result.error?.message || 'Error al actualizar el estado de verificación');
      }
    } catch (err) {
      setError('Error de conexión al actualizar');
    } finally {
      setUpdatingCampaign(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; text: string }> = {
      active: { color: 'bg-green-100 text-green-800', text: 'Activa' },
      expired: { color: 'bg-orange-100 text-orange-800', text: 'Vencida' },
      completed: { color: 'bg-blue-100 text-blue-800', text: 'Completada' },
      cancelled: { color: 'bg-red-100 text-red-800', text: 'Cancelada' },
    };
    const config = statusConfig[status] || statusConfig.active;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const getTypeBadge = (type: string) => {
    const typeConfig: Record<string, { color: string; text: string }> = {
      solidarity: { color: 'bg-purple-100 text-purple-800', text: 'Solidaria' },
      family: { color: 'bg-pink-100 text-pink-800', text: 'Familiar' },
      celebration: { color: 'bg-yellow-100 text-yellow-800', text: 'Celebración' },
    };
    const config = typeConfig[type] || typeConfig.solidarity;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        {config.text}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando panel de administración...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Volver al dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Panel de Administración</h1>
                <p className="text-gray-600">Gestiona y verifica campañas de DonaApp</p>
              </div>
            </div>
            <div className="text-sm text-gray-500">
              Total: {campaigns.length} campañas
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
            <p className="text-sm text-red-700 font-medium">{error}</p>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <Eye className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Campañas</p>
                <p className="text-2xl font-bold text-gray-900">{campaigns.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <Check className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Verificadas</p>
                <p className="text-2xl font-bold text-gray-900">
                  {campaigns.filter(c => c.isVerified).length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <X className="h-8 w-8 text-yellow-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Pendientes</p>
                <p className="text-2xl font-bold text-gray-900">
                  {campaigns.filter(c => !c.isVerified).length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <Calendar className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Activas</p>
                <p className="text-2xl font-bold text-gray-900">
                  {campaigns.filter(c => c.status === 'active').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 mb-6 bg-white rounded-lg border border-gray-200 p-1">
          <button
            onClick={() => { setActiveTab('campaigns'); setSearchParams({}); }}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'campaigns' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            Campañas ({campaigns.length})
          </button>
          <button
            onClick={() => { setActiveTab('reports'); setSearchParams({ tab: 'reports' }); }}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors relative ${
              activeTab === 'reports' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            Reportes
            {pendingReportsCount > 0 && (
              <span className={`ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full ${
                activeTab === 'reports' ? 'bg-white text-indigo-600' : 'bg-red-500 text-white'
              }`}>
                {pendingReportsCount}
              </span>
            )}
          </button>
        </div>

        {/* Campaigns Table */}
        {activeTab === 'campaigns' && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Todas las Campañas</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaña</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Creador</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ubicación</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verificación</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {campaigns.map((campaign) => (
                  <tr key={campaign.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{campaign.title}</div>
                      <div className="text-sm text-gray-500 max-w-xs truncate">{campaign.description}</div>
                      <div className="flex items-center mt-1 space-x-2">
                        {getTypeBadge(campaign.campaignType)}
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          campaign.privacyMode === 'public' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {campaign.privacyMode === 'public' ? 'Pública' : 'Privada'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <User className="h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{campaign.creatorName || 'Sin nombre'}</div>
                          <div className="text-sm text-gray-500">{campaign.creatorEmail}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm text-gray-900">{campaign.location.city}, {campaign.location.state}</div>
                          <div className="text-sm text-gray-500">{campaign.location.country}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(campaign.status)}
                      <div className="text-xs text-gray-500 mt-1">
                        Creada: {formatDate(campaign.createdAt.toString())}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        campaign.isVerified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {campaign.isVerified ? '✅ Verificada' : '⏳ Pendiente'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium">
                      <div className="flex space-x-2">
                        {!campaign.isVerified ? (
                          <button
                            onClick={() => updateVerificationStatus(campaign.id, true)}
                            disabled={updatingCampaign === campaign.id}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                          >
                            {updatingCampaign === campaign.id ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                            ) : (
                              <Check className="h-3 w-3 mr-1" />
                            )}
                            Verificar
                          </button>
                        ) : (
                          <button
                            onClick={() => updateVerificationStatus(campaign.id, false)}
                            disabled={updatingCampaign === campaign.id}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                          >
                            {updatingCampaign === campaign.id ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                            ) : (
                              <X className="h-3 w-3 mr-1" />
                            )}
                            Quitar
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/campaigns/${campaign.id}`)}
                          className="inline-flex items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          Ver
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {campaigns.length === 0 && !error && (
            <div className="text-center py-12">
              <p className="text-gray-500">No hay campañas para mostrar</p>
            </div>
          )}
        </div>
        )}

        {/* Reports Section */}
        {activeTab === 'reports' && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Reportes de Campañas</h2>
          </div>

          {reports.length === 0 ? (
            <div className="text-center py-12">
              <Flag className="w-8 h-8 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No hay reportes</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {reports.map((report) => (
                <div key={report.id} className={`px-6 py-4 ${report.status === 'pending' ? 'bg-amber-50/50' : ''}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      {/* Campaign link */}
                      <div className="flex items-center space-x-2 mb-1">
                        <button
                          onClick={() => navigate(`/campaigns/${report.campaignId}`)}
                          className="text-sm font-medium text-indigo-600 hover:text-indigo-800 truncate"
                        >
                          {report.campaignTitle}
                        </button>
                        <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${
                          report.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                          report.status === 'reviewed' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {report.status === 'pending' ? 'Pendiente' : report.status === 'reviewed' ? 'Revisado' : 'Desestimado'}
                        </span>
                      </div>

                      {/* Reason badge */}
                      <div className="flex items-center space-x-2 mb-2">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          report.reason === 'fraud' ? 'bg-red-100 text-red-800' :
                          report.reason === 'unfulfilled' ? 'bg-orange-100 text-orange-800' :
                          report.reason === 'inappropriate' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {report.reason === 'unfulfilled' ? 'No cumplió' :
                           report.reason === 'inappropriate' ? 'Inapropiado' :
                           report.reason === 'fraud' ? 'Fraude' : 'Otro'}
                        </span>
                        <span className="text-xs text-gray-500">
                          por {report.reporterName} ({report.reporterEmail})
                        </span>
                      </div>

                      {/* Comment */}
                      <p className="text-sm text-gray-700 mb-1">"{report.comment}"</p>

                      {/* Meta */}
                      <p className="text-xs text-gray-400">
                        {new Date(report.createdAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>

                      {/* Admin note */}
                      {report.adminNote && (
                        <p className="text-xs text-gray-500 mt-1 italic">Nota: {report.adminNote}</p>
                      )}
                    </div>

                    {/* Actions */}
                    {report.status === 'pending' && (
                      <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                        <button
                          onClick={() => resolveReport(report.id, 'reviewed')}
                          disabled={updatingReport === report.id}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                        >
                          <Check className="w-3 h-3 mr-1" />
                          Revisado
                        </button>
                        <button
                          onClick={() => resolveReport(report.id, 'dismissed')}
                          disabled={updatingReport === report.id}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-gray-500 hover:bg-gray-600 disabled:opacity-50"
                        >
                          <X className="w-3 h-3 mr-1" />
                          Desestimar
                        </button>
                        <button
                          onClick={() => navigate(`/campaigns/${report.campaignId}`)}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Ver
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
